'use client'

import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'

import { cn } from '@/lib/utils'

function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  )
}

/** How far a hidden tab sits from the edge once it is scrolled into view. */
const REVEAL_MARGIN_PX = 24

/**
 * Keeps a tab row that is wider than its space on one line: it scrolls
 * sideways, fades at an edge with more tabs past it, and brings the active tab
 * into view, on first draw and whenever another tab is picked.
 */
function useScrollingTabs(list: React.RefObject<HTMLDivElement | null>) {
  React.useEffect(() => {
    const element = list.current
    if (!element) return

    function edges() {
      const node = element!
      const hidden = node.scrollWidth - node.clientWidth
      node.toggleAttribute('data-fade-start', hidden > 1 && node.scrollLeft > 1)
      node.toggleAttribute('data-fade-end', hidden > 1 && node.scrollLeft < hidden - 1)
    }

    function reveal(smooth: boolean) {
      const node = element!
      const active = node.querySelector<HTMLElement>('[role="tab"][data-state="active"]')
      if (!active || node.scrollWidth <= node.clientWidth) return
      const start = active.offsetLeft - REVEAL_MARGIN_PX
      const end = active.offsetLeft + active.offsetWidth + REVEAL_MARGIN_PX - node.clientWidth
      const left = node.scrollLeft > start ? start : node.scrollLeft < end ? end : null
      if (left === null) return
      const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      node.scrollTo({ left, behavior: smooth && !still ? 'smooth' : 'auto' })
    }

    edges()
    reveal(false)
    element.addEventListener('scroll', edges, { passive: true })
    const resized = new ResizeObserver(() => {
      edges()
      reveal(false)
    })
    resized.observe(element)
    const picked = new MutationObserver(() => reveal(true))
    picked.observe(element, { subtree: true, attributes: true, attributeFilter: ['data-state'] })
    return () => {
      element.removeEventListener('scroll', edges)
      resized.disconnect()
      picked.disconnect()
    }
  }, [list])
}

function TabsList({ className, ref, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  const list = React.useRef<HTMLDivElement | null>(null)
  useScrollingTabs(list)
  const setRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      list.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref],
  )

  return (
    <TabsPrimitive.List
      ref={setRef}
      data-slot="tabs-list"
      className={cn(
        'bg-muted text-muted-foreground relative inline-flex h-9 w-fit max-w-full items-center justify-center-safe overflow-x-auto overscroll-x-contain rounded-lg p-[3px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        // A row too wide for its space scrolls; the edge with more tabs past it fades.
        '[--fade-end:0px] [--fade-start:0px] data-[fade-end]:[--fade-end:2rem] data-[fade-start]:[--fade-start:2rem] [mask-image:linear-gradient(to_right,transparent,#000_var(--fade-start),#000_calc(100%-var(--fade-end)),transparent)]',
        className,
      )}
      {...props}
    />
  )
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "data-[state=active]:bg-background dark:data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  )
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
